import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { navigate, graphql } from 'gatsby';
import { useLocation } from '@gatsbyjs/reach-router';
import Wrapper from './wrapper';
import ButtonErrorWrapper from './button-error-wrapper';
import StyledForm from './form-component';
import Button from './button';
import Title from './title';
import Field from './field';
import Intro from './intro';
import ErrorAlert from './alert/error';
import SuccessAlert from './alert/success';
import useCreateSubmission from '../hooks/use-create-submission';

// Use this fragment in your pageQuery
// and use it as <Form {...page.form} />
export const query = graphql`
  fragment StrapiForm on STRAPI_FORM {
    id
    documentId
    strapiId: strapi_id
    name
    title
    buttonText
    redirectAfterSuccess
    successMessage
    intro {
      data {
        childMarkdownRemark {
          html
        }
      }
    }

    components {
      typename: __typename

      ...on STRAPI__COMPONENT_FORMS_INPUT {
        id
        name
        label
        placeholder
        required
        type
      }
      ...on STRAPI__COMPONENT_FORMS_SELECT {
        id
        name
        label
        required
        values {
          value
          label
        }
        defaultValue
        type
      }

      ...on STRAPI__COMPONENT_FORMS_DATE {
        id
        name
        label
        value
        required
        type
        datepicker {
          minDaysFromNow
          limitedDaysAfterMinDaysFromNow
          min
          max
          excludeSaturdays
          excludeSundays
          excludeDates {
            date(formatString: "YYYY-MM-DDTHH:mm:ssZ")
          }
          note
        }
      }

      ...on STRAPI__COMPONENT_FORMS_MESSAGE {
        id
        content {
          data {
            childMarkdownRemark {
              html
            }
          }
        }
      }

      ...on STRAPI__COMPONENT_FORMS_EMPLOYEES {
        id
        name
        label
        required
        employees {
          id
          name
        }
      }

      ...on STRAPI__COMPONENT_FORMS_VEHICLES {
        id
        name
        label
        required
      }
      ... on STRAPI__COMPONENT_FORMS_LICENSE_PLATE {
        id
        name
        label
        required
      }

      ...on STRAPI__COMPONENT_FORMS_COMPANIES {
        id
        name
        label
        required
        withDepartments {
          id
          label
          required
        }
        companies {
          id
          documentId
          strapiId: strapi_id
          name
          departments {
            id
            name
            email
          }
        }
      }
    }
  }
`;

const Form = ({
  strapiId, // v4
  documentId, // v5
  name,
  title,
  intro,
  buttonText,
  successMessage,
  components = [],
  redirectAfterSuccess = null,
  removeFormWhenSuccess = true,
  successProps = {},
  horizontal = false,
  standout = false,
  buttonProps = {},
  justify,
  className,
  ...props
}) => {
  // allow setting values via a Link
  const { state } = useLocation();
  // remove the key that gatsby always adds
  // remove the lead from initial values, it is handled in the submission hook
  const {
    key: _,
    via: __,
    lead: ___,
    ...locationState
  } = state || {};
  // get all defaultValues ready
  const initialFormFields = (components || []).reduce((acc, field) => {
    // message doesnt need state
    // companies will make relations itself
    if (['STRAPI__COMPONENT_FORMS_MESSAGE', 'STRAPI__COMPONENT_FORMS_COMPANIES'].includes(field.typename)) {
      return acc;
    }
    if (field.defaultValue) {
      return ({
        ...acc,
        [field.name]: field.defaultValue,
      });
    }
    if (field.required) {
      return ({
        ...acc,
        [field.name]: field.values?.[0]?.value || '',
      });
    }
    return ({
      ...acc,
      [field.name]: '',
    });
  }, {});
  // the initial form state is defaultValues and what is pushed in the location state
  const [formValues, setValues] = useState({
    ...initialFormFields,
    ...locationState,
  });

  const onChange = useCallback((fieldName) => (e) => {
    setValues((formState) => ({
      ...formState,
      [fieldName]: e.target.value,
    }));
  }, []);

  const onToggle = useCallback((fieldName, options) => (e) => {
    setValues((formState) => {
      const value = ([].concat(formState[fieldName]) || [])
        .filter((savedValue) => options
          .some(({ value: allowedValue }) => allowedValue === savedValue));

      return {
        ...formState,
        [fieldName]: value.includes(e.target.value)
          ? value.filter((val) => val !== e.target.value)
          : value.concat(e.target.value),
      };
    });
  }, []);

  const {
    onSubmit,
    data,
    lead,
    loading,
    error,
  } = useCreateSubmission({
    fields: formValues, // pass everything as fields and let the submission lifecycle handle it
  }, name, {
    formId: documentId || strapiId,
    ctaText: buttonText,
  });

  if (redirectAfterSuccess && data) {
    if (redirectAfterSuccess.startsWith('http')) {
      navigate(redirectAfterSuccess);
      return null;
    }
    const prependSlash = !redirectAfterSuccess.startsWith('/') ? `/${redirectAfterSuccess}` : redirectAfterSuccess;
    const appendSlash = !prependSlash.endsWith('/') ? `${prependSlash}/` : prependSlash;
    navigate(appendSlash, {
      state: {
        lead,
      },
    });
    return null;
  }

  const showForm = !removeFormWhenSuccess || !(removeFormWhenSuccess && data);

  return (
    <Wrapper className={className} name={name}>
      {title && <Title>{title}</Title>}
      {intro?.data?.childMarkdownRemark?.html && <Intro $content={intro} />}
      {showForm && (
        <StyledForm
          {...props}
          onSubmit={onSubmit}
          $horizontal={horizontal}
          $standout={standout}
        >
          <input type="hidden" name="form-name" value={name} />
          {(components || []).map((field) => (
            <Field
              key={field.id}
              {...field}
              onChange={onChange(field.name)}
              rawOnChange={onChange}
              onToggle={onToggle(field.name, field.values)}
              value={formValues[field.name]}
              horizontal={horizontal}
              standout={standout}
            />
          ))}
          <ButtonErrorWrapper $standout={standout} justify={justify}>
            {data && (
              <SuccessAlert
                {...successProps}
                data={data}
                $horizontal={horizontal}
                successMessage={successMessage}
                $standout={standout}
              />
            )}
            {error && (
              <ErrorAlert error={error} />
            )}
            <Button
              type="submit"
              disabled={loading}
              wide={horizontal}
              $horizontal={horizontal}
              $standout={standout}
              {...buttonProps}
            >
              {buttonText}
            </Button>
          </ButtonErrorWrapper>
        </StyledForm>
      )}
      {data && !showForm && (
        <SuccessAlert
          {...successProps}
          data={data}
          $horizontal={horizontal}
          successMessage={successMessage}
          $standout={standout}
        />
      )}
    </Wrapper>
  );
};

export default styled(Form)``;
